'use client'

import { useCallback, useEffect, useState } from "react"

/** Mirrors DOMAIN_CART_MAX_ITEMS / DOMAIN_MAX_PERIOD in the backend service. */
export const CART_MAX_ITEMS = 10
/**
 * TEMPORARY: registration is capped at a single year.
 *
 * Mirrors DOMAIN_MAX_PURCHASE_PERIOD in the backend's lib/constant.js, which is
 * the authority — the server rejects anything above it regardless of what this
 * says, so lowering it here alone would only hide the option, not enforce it.
 *
 * Multi-year is fully built: every line carries its registry's `allowedYears`
 * and the term selectors below already read it. Raise this to 10 in step with
 * the backend constant and the selectors reappear on their own, because each
 * one is rendered only when there is more than one term to choose.
 */
export const CART_MAX_PERIOD = 1

const STORAGE_KEY = 'shopmn.domain.cart'
/** Fired on write so every mounted cart (header badge, sheet) re-reads. */
const CART_EVENT = 'shopmn:domain-cart'
/** Fired to open the cart sheet from anywhere without threading state through. */
const CART_OPEN_EVENT = 'shopmn:domain-cart-open'

/**
 * Open the cart sheet from any client component.
 *
 * An event rather than context: the sheet is mounted once in the header, and the
 * only other caller is the search result card. A provider around the whole app
 * would be more machinery than one button needs.
 */
export function openCart() {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new CustomEvent(CART_OPEN_EVENT))
}

/**
 * Where a signed-out buyer goes, and where login puts them back.
 *
 * Checkout needs an account, but a button labelled "buy" must still advance
 * toward payment rather than quietly filling the basket and stopping. The name
 * is parked in the basket first - it lives in localStorage, so it survives the
 * round trip - and `callbackUrl` returns to the page that basket is rendered
 * on, where the pay button is waiting. A literal `/` because every caller sits
 * on the public home page; there is no other page this basket appears on.
 */
export const CART_LOGIN_HREF = '/login?callbackUrl=/'

/**
 * ДАН verification, which the backend requires before it will issue a domain
 * invoice. The page it points at is also where ДАН returns the browser, so the
 * detour begins and ends in the same place.
 */
export const DAN_VERIFY_HREF = '/dan/status'

/**
 * What stands between a buy click and a payment, as a URL to send them to -
 * or null when nothing does and checkout can run.
 *
 * Both conditions are enforced server-side at checkout regardless of what this
 * returns; this exists so the answer arrives as a next step rather than as an
 * error toast at the end of the flow. Order matters: someone signed out has no
 * ДАН status to read, so login is always the first hop.
 */
export function checkoutBlocker({ signedIn, danVerified }: { signedIn: boolean, danVerified: boolean }): string | null {
  if (!signedIn) return CART_LOGIN_HREF
  if (!danVerified) return DAN_VERIFY_HREF
  return null
}

export function onCartOpen(handler: () => void) {
  window.addEventListener(CART_OPEN_EVENT, handler)
  return () => window.removeEventListener(CART_OPEN_EVENT, handler)
}

export type CartItem = {
  domain: string
  /** Registration term in years. Valid values are `allowedYears` when set,
   *  otherwise 1..CART_MAX_PERIOD. */
  period: number
  /** The registry's discrete set of sellable terms, e.g. `[1, 3, 5, 8, 10]`.
   *  `null` (or absent) means the full 1..CART_MAX_PERIOD range - what `.mn`
   *  sends. Carried per line because every TLD publishes its own set; a flat
   *  1-10 range would let someone pay for a term the registry then rejects. */
  allowedYears?: number[] | null
}

/** Every year this cart offers when a line has no discrete `allowedYears`. */
const FULL_RANGE = Array.from({ length: CART_MAX_PERIOD }, (_, i) => i + 1)

function sanitizeAllowedYears(value: any): number[] | null {
  if (!Array.isArray(value)) return null
  const years = value.filter((y) => Number.isInteger(y) && y >= 1 && y <= CART_MAX_PERIOD)
  return years.length > 0 ? years : null
}

function isValidPeriod(period: number, allowedYears?: number[] | null): boolean {
  if (!Number.isInteger(period)) return false
  if (allowedYears && allowedYears.length > 0) return allowedYears.includes(period)
  return period >= 1 && period <= CART_MAX_PERIOD
}

/**
 * The period selector's choices for one cart line: the registry's own discrete
 * terms when the line carries them, otherwise every year 1..CART_MAX_PERIOD.
 * Shared by every period `Select` in the app so a registry's rule reaches the
 * control everywhere a line is priced, not just where it was added.
 */
export function periodItems(allowedYears?: number[] | null) {
  const years = allowedYears && allowedYears.length > 0 ? allowedYears : FULL_RANGE
  // A registry's own set can run past CART_MAX_PERIOD — .com publishes 1..10 —
  // so it is capped here too. Without this, `allowedYears` silently overrode the
  // one-year limit for every non-.mn line and the selector reappeared.
  const capped = years.filter((y) => y <= CART_MAX_PERIOD)
  // Never empty: a registry with no term at or below the cap still has to offer
  // something, and its shortest term is the honest choice.
  const offered = capped.length > 0 ? capped : [years[0]]
  return offered.map((y) => ({ value: String(y), label: `${y} жил` }))
}

function read(): CartItem[] {
  if (typeof window === 'undefined') return []
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    // Anything hand-edited or written by an older build is discarded rather than
    // trusted - this feeds a checkout.
    return parsed
      .filter((i: any) => i && typeof i.domain === 'string')
      .map((i: any) => {
        const allowedYears = sanitizeAllowedYears(i.allowedYears)
        const period = isValidPeriod(i.period, allowedYears) ? i.period : (allowedYears ? allowedYears[0] : 1)
        return { domain: i.domain, period, allowedYears }
      })
      .slice(0, CART_MAX_ITEMS)
  } catch {
    return []
  }
}

function write(items: CartItem[]) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items))
  } catch {
    // Private mode or a full quota. The in-memory cart still works for this
    // page view; losing it on reload beats crashing the page.
  }
  window.dispatchEvent(new CustomEvent(CART_EVENT))
}

/**
 * The basket, held in localStorage.
 *
 * Nothing is reserved until payment, so there is no server state to keep in
 * sync - availability and price are re-checked at checkout and again at
 * settlement regardless of what this holds.
 */
export function useDomainCart() {
  // Starts empty rather than reading storage during render: the server has no
  // localStorage, so seeding from it would produce a hydration mismatch.
  const [items, setItems] = useState<CartItem[]>([])
  const [loaded, setLoaded] = useState(false)

  // Genuine external-system sync - a browser API, plus cross-tab and
  // cross-component updates. Not derived state.
  useEffect(() => {
    const sync = () => setItems(read())
    sync()
    setLoaded(true)
    window.addEventListener(CART_EVENT, sync)
    window.addEventListener('storage', sync)
    return () => {
      window.removeEventListener(CART_EVENT, sync)
      window.removeEventListener('storage', sync)
    }
  }, [])

  const add = useCallback((domain: string, period = 1, allowedYears: number[] | null = null) => {
    const current = read()
    if (current.some(i => i.domain === domain)) return { ok: false, reason: 'duplicate' as const }
    if (current.length >= CART_MAX_ITEMS) return { ok: false, reason: 'full' as const }
    const cleanAllowedYears = sanitizeAllowedYears(allowedYears)
    const cleanPeriod = isValidPeriod(period, cleanAllowedYears) ? period : (cleanAllowedYears ? cleanAllowedYears[0] : 1)
    write([...current, { domain, period: cleanPeriod, allowedYears: cleanAllowedYears }])
    return { ok: true as const, reason: null }
  }, [])

  const remove = useCallback((domain: string) => {
    write(read().filter(i => i.domain !== domain))
  }, [])

  const setPeriod = useCallback((domain: string, period: number) => {
    write(read().map(i => {
      if (i.domain !== domain) return i
      if (!isValidPeriod(period, i.allowedYears)) return i
      return { ...i, period }
    }))
  }, [])

  const clear = useCallback(() => write([]), [])

  const has = useCallback((domain: string) => items.some(i => i.domain === domain), [items])

  return { items, count: items.length, loaded, add, remove, setPeriod, clear, has }
}
